import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearch } from '@tanstack/react-router';
import { ApiError, api, type OrganizationRecord } from '../lib/api';
import { Empty, ErrorNote, PageHeading, Spinner } from '../components/ui';
import { Confirm } from '../components/Confirm';
import {
  Ledger,
  LedgerToolbar,
  onStage,
  statusFacet,
  searchRows,
  type LedgerColumn,
} from '../components/Ledger';
import { useListView } from '../lib/useListView';

/*
 * A factory rather than a constant, because the actions cell needs the page's
 * mutations. It still lives out here so the cell renderers are not rebuilt on
 * every render of the page.
 */
function columnsFor(actions: {
  /** Suspend asks first and waits on this; Restore is one click. */
  onSetActive: (org: OrganizationRecord) => Promise<unknown>;
  busy: boolean;
}): LedgerColumn<OrganizationRecord>[] {
  return [
    {
      id: 'name',
      header: 'Organization',
      sort: (org) => org.name,
      search: (org) => org.name,
      className: 'font-medium',
      cell: (org) => (
        <span className={org.isActive ? '' : 'text-ink-faint line-through'}>{org.name}</span>
      ),
    },
    {
      id: 'slug',
      header: 'Sign-in link',
      sort: (org) => org.slug,
      search: (org) => org.slug,
      className: 'font-mono text-xs text-ink-soft',
      cell: (org) => `/o/${org.slug}/login`,
    },
    {
      id: 'timezone',
      header: 'Timezone',
      sort: (org) => org.timezone,
      search: (org) => org.timezone,
      className: 'font-mono text-xs text-ink-soft',
      cell: (org) => org.timezone,
    },
    {
      id: 'staff',
      header: 'Staff',
      numeric: true,
      sort: (org) => org.userCount,
      className: 'font-mono tabular',
      cell: (org) => org.userCount,
    },
    {
      id: 'students',
      header: 'Students',
      numeric: true,
      sort: (org) => org.studentCount,
      className: 'font-mono tabular',
      cell: (org) => org.studentCount,
    },
    {
      id: 'actions',
      header: 'Actions',
      headerHidden: true,
      align: 'right',
      cell: (org) => (
        <div className="flex flex-wrap justify-end gap-2">
          {org.isActive ? (
            <Confirm
              trigger="Suspend"
              title={`Suspend ${org.name}?`}
              description="Everyone at this organization is locked out at sign-in until it is restored. Nothing is deleted."
              confirmLabel="Suspend"
              pendingLabel="Suspending…"
              disabled={actions.busy}
              onConfirm={() => actions.onSetActive(org)}
              errorFallback="Could not suspend that organization."
            />
          ) : (
            <button
              type="button"
              className="btn"
              onClick={() => void actions.onSetActive(org).catch(() => {})}
              disabled={actions.busy}
            >
              Restore
            </button>
          )}
        </div>
      ),
    },
  ];
}

export function Platform() {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    adminEmail: '',
    adminName: '',
    adminPassword: '',
  });

  const orgs = useQuery({ queryKey: ['organizations'], queryFn: api.platform.organizations });

  const create = useMutation({
    mutationFn: () =>
      api.platform.createOrganization({
        name: form.name,
        slug: form.slug,
        timezone: form.timezone,
        ...(form.adminEmail
          ? {
              admin: {
                email: form.adminEmail,
                name: form.adminName || form.adminEmail,
                password: form.adminPassword,
              },
            }
          : {}),
      }),
    onSuccess: async () => {
      setCreating(false);
      setForm({ ...form, name: '', slug: '', adminEmail: '', adminName: '', adminPassword: '' });
      await queryClient.invalidateQueries({ queryKey: ['organizations'] });
    },
  });

  /** Suspending an organization locks out its entire staff at sign-in. */
  const setActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.platform.updateOrganization(id, { isActive }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['organizations'] }),
  });

  const search = useSearch({ from: '/app/platform' });
  const columns = useMemo(
    () =>
      columnsFor({
        onSetActive: (org) => setActive.mutateAsync({ id: org.id, isActive: !org.isActive }),
        busy: setActive.isPending,
      }),
    [setActive],
  );
  const view = useListView({ search, columns });
  const show = view.get('show');
  const [shut, setShut] = useState<ReadonlySet<string>>(new Set());

  const all = useMemo(() => orgs.data?.organizations ?? [], [orgs.data]);
  const rows = useMemo(() => {
    const live = onStage(all, show);
    return searchRows(live, columns, view.query);
  }, [all, columns, show, view.query]);

  return (
    <div>
      <PageHeading eyebrow="Every tenant on this deployment" title="Organizations">
        <button type="button" className="btn btn-primary" onClick={() => setCreating((v) => !v)}>
          {creating ? 'Cancel' : 'New organization'}
        </button>
      </PageHeading>

      {creating ? (
        <form
          className="panel mb-5 grid gap-3 p-5 md:grid-cols-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          {create.error ? (
            <div className="md:col-span-3">
              <ErrorNote
                message={
                  create.error instanceof ApiError
                    ? create.error.message
                    : 'Could not create organization.'
                }
              />
            </div>
          ) : null}
          <label className="grid gap-1.5">
            <span className="eyebrow">Name</span>
            <input
              className="field"
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Grace Church"
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Short name (used in the sign-in link)</span>
            <input
              className="field font-mono"
              required
              pattern="[a-z0-9]+(-[a-z0-9]+)*"
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value.toLowerCase() })}
              placeholder="grace"
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Timezone</span>
            <input
              className="field font-mono"
              required
              value={form.timezone}
              onChange={(e) => setForm({ ...form, timezone: e.target.value })}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">First administrator email</span>
            <input
              className="field"
              type="email"
              value={form.adminEmail}
              onChange={(e) => setForm({ ...form, adminEmail: e.target.value })}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Administrator name</span>
            <input
              className="field"
              value={form.adminName}
              onChange={(e) => setForm({ ...form, adminName: e.target.value })}
            />
          </label>
          <label className="grid gap-1.5">
            <span className="eyebrow">Temporary password</span>
            <input
              className="field font-mono"
              minLength={12}
              value={form.adminPassword}
              onChange={(e) => setForm({ ...form, adminPassword: e.target.value })}
              placeholder="at least 12 characters"
            />
          </label>
          <div className="md:col-span-3">
            <button className="btn btn-primary" type="submit" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Create organization'}
            </button>
          </div>
        </form>
      ) : null}

      {orgs.isLoading ? (
        <Spinner />
      ) : (
        <>
          <LedgerToolbar
            facets={[
              statusFacet({
                /*
                 * Two chips, not three. An organization cannot be deleted: the
                 * policy that hides deleted rows is scoped to one tenant, so it
                 * has nothing to say about the tenants themselves.
                 */
                label: 'Filter organizations by status',
                liveLabel: 'Active',
                inactiveLabel: 'Suspended',
                value: show,
                onChange: (next) => view.set({ show: next || undefined }),
                live: onStage(all, '').length,
                inactive: onStage(all, 'inactive').length,
              }),
            ]}
            query={view.draft}
            onQuery={view.onQuery}
            searchPlaceholder="name, link or timezone"
            shown={rows.length}
            total={all.length}
          />
          <Ledger
            caption="Organizations"
            rows={rows}
            total={all.length}
            columns={columns}
            rowId={(org) => org.id}
            sort={view.sort}
            onSort={view.onSort}
            collapsed={shut}
            onCollapse={(id, closed) =>
              setShut((prev) => {
                const next = new Set(prev);
                if (closed) next.add(id);
                else next.delete(id);
                return next;
              })
            }
            empty={
              <Empty
                title="No organizations yet"
                hint="Create the first one to start taking registers."
              />
            }
            noMatch={
              <Empty
                title="Nothing to show"
                hint={
                  view.query
                    ? 'No organization matches that name, link or timezone.'
                    : 'They are all suspended. Switch to All to see them.'
                }
              />
            }
          />
        </>
      )}
    </div>
  );
}
